import React from "react";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import InputLabel from "@mui/material/InputLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import { useDispatch, useSelector } from "react-redux";
import {
  API_BASE,
  useUploadFileMutation,
  useStartCsvConversionMutation,
  downloadCsvConversionJob,
  useConvertUrlQuery,
} from "../../store/api";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import MuiAlert from "@mui/material/Alert";
import { setFile } from "../../store/app";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import TideLookup from "./TideLookup";

export default function Converter() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const file = useSelector((state) => state.app.file);
  const [csbFile, setCsbFile] = React.useState(undefined);
  const [url, setUrl] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const [csvOutputMode, setCsvOutputMode] = React.useState("separate");
  const [correctMllw, setCorrectMllw] = React.useState(false);
  const [smoothDepthSpikes, setSmoothDepthSpikes] = React.useState(false);
  const [applyWaterLevelCorrection, setApplyWaterLevelCorrection] =
    React.useState(false);
  const [applyMaxDepthCutoff, setApplyMaxDepthCutoff] = React.useState(false);
  const [maxDepthCutoffFt, setMaxDepthCutoffFt] = React.useState("30");
  const [filterTracksNearArea, setFilterTracksNearArea] = React.useState(false);
  const [filterRadiusNm, setFilterRadiusNm] = React.useState("8");
  const [gpxEleUnits, setGpxEleUnits] = React.useState("auto");
  const [tideCenterLat, setTideCenterLat] = React.useState("");
  const [tideCenterLon, setTideCenterLon] = React.useState("");
  const [csvSummary, setCsvSummary] = React.useState(undefined);
  const [csvJobId, setCsvJobId] = React.useState(undefined);
  const [gpxSubmitting, setGpxSubmitting] = React.useState(false);
  const [csvProgress, setCsvProgress] = React.useState(undefined);
  const [csvError, setCsvError] = React.useState(undefined);
  const [submitUrl, setSubmitUrl] = React.useState(undefined);

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleClose = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const [upload] = useUploadFileMutation();
  const [startCsvConversion] = useStartCsvConversionMutation();
  const { data } = useConvertUrlQuery(submitUrl ? url : skipToken);

  const csvConverting = Boolean(csvJobId);

  const isCsvFile = (file) =>
    file &&
    (file.name.toLowerCase().endsWith(".csv") || file.type === "text/csv");

  const isGpxFile = (file) =>
    file &&
    (file.name.toLowerCase().endsWith(".gpx") ||
      file.type === "application/gpx+xml");

  const parseFilename = (disposition) => {
    const match = /filename=([^;]+)/.exec(disposition || "");
    return match ? match[1].trim().replace(/"/g, "") : "tracks.gpx";
  };

  const downloadBlob = (filename, blob) => {
    const url = window.URL.createObjectURL(blob);
    const element = document.createElement("a");
    element.href = url;
    element.download = filename;
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    window.URL.revokeObjectURL(url);
  };

  const formatTimestamp = (value) => {
    const date = new Date(value.replace(" ", "T") + "Z");
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const formatDepth = (value) => `${value.toFixed(1)} ft`;

  // Prefill the area-of-interest fields with the center of the track extent.
  // The user should still adjust this to their actual work area, since CSB
  // exports include pass-through tracks that stretch the bounding box.
  const prefillAreaOfInterest = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      if (typeof text !== "string") {
        return;
      }
      const lines = text.split(/\r?\n/);
      if (lines.length < 2) {
        return;
      }
      const header = lines[0]
        .split(",")
        // NOAA CSB exports include a UTF-8 BOM on the first column, and some
        // exports quote headers. Strip both so "lon"/"lat" match.
        .map((column) =>
          column
            .replace(/^\ufeff/, "")
            .trim()
            .replace(/^"|"$/g, "")
            .toLowerCase()
        );
      const latIndex = header.findIndex(
        (column) => column === "lat" || column === "latitude"
      );
      const lonIndex = header.findIndex(
        (column) =>
          column === "lon" || column === "lng" || column === "longitude"
      );
      if (latIndex < 0 || lonIndex < 0) {
        return;
      }
      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLon = Infinity;
      let maxLon = -Infinity;
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row) {
          continue;
        }
        const cells = row.split(",");
        const lat = parseFloat((cells[latIndex] || "").replace(/"/g, ""));
        const lon = parseFloat((cells[lonIndex] || "").replace(/"/g, ""));
        if (Number.isNaN(lat) || Number.isNaN(lon)) {
          continue;
        }
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
      }
      if (minLat > maxLat || minLon > maxLon) {
        return;
      }
      setTideCenterLat(((minLat + maxLat) / 2).toFixed(4));
      setTideCenterLon(((minLon + maxLon) / 2).toFixed(4));
    };
    reader.readAsText(file);
  };

  const handleFile = (event) => {
    const file = event.target.files[0];
    setCsbFile(file);
    setCsvSummary(undefined);
    setCsvError(undefined);
    if (isCsvFile(file)) {
      prefillAreaOfInterest(file);
    } else if (isGpxFile(file)) {
      // GPX MLLW uses per-point ICW station lookup unless both fields are filled.
      // Clear any coordinates left over from a prior CSV upload.
      setTideCenterLat("");
      setTideCenterLon("");
    }
  };

  const downloadFile = (filename, text) => {
    var element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:application/xml;charset=utf-8," + encodeURIComponent(text)
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const needsAreaOfInterest =
    isCsvFile(csbFile) &&
    (correctMllw || applyWaterLevelCorrection || filterTracksNearArea);

  const appendMaxDepthParams = (formData) => {
    formData.append(
      "applyMaxDepthCutoff",
      applyMaxDepthCutoff ? "true" : "false"
    );
    if (applyMaxDepthCutoff) {
      formData.append("maxDepthCutoffFt", parseFloat(maxDepthCutoffFt));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setOpen(true);
    setCsvSummary(undefined);
    setCsvError(undefined);
    if (csbFile) {
      if (applyMaxDepthCutoff) {
        const cutoffNum = parseFloat(maxDepthCutoffFt);
        if (Number.isNaN(cutoffNum) || cutoffNum <= 0) {
          setCsvError("Enter a valid max depth cutoff in feet.");
          setOpen(false);
          return;
        }
      }

      if (isCsvFile(csbFile)) {
        if (needsAreaOfInterest) {
          const latNum = parseFloat(tideCenterLat);
          const lonNum = parseFloat(tideCenterLon);
          if (Number.isNaN(latNum) || Number.isNaN(lonNum)) {
            setCsvError(
              "Enter the latitude and longitude of your area of interest."
            );
            setOpen(false);
            return;
          }
          if (filterTracksNearArea) {
            const radiusNum = parseFloat(filterRadiusNm);
            if (Number.isNaN(radiusNum) || radiusNum <= 0) {
              setCsvError(
                "Enter a valid track filter radius in nautical miles."
              );
              setOpen(false);
              return;
            }
          }
        }

        const formData = new FormData();
        formData.append("file", csbFile);
        formData.append("fileName", csbFile.name);
        formData.append("outputMode", csvOutputMode);
        formData.append("correctMllw", correctMllw ? "true" : "false");
        formData.append(
          "smoothDepthSpikes",
          smoothDepthSpikes ? "true" : "false"
        );
        formData.append(
          "applyWaterLevelCorrection",
          applyWaterLevelCorrection ? "true" : "false"
        );
        formData.append(
          "filterTracksNearArea",
          filterTracksNearArea ? "true" : "false"
        );
        if (needsAreaOfInterest) {
          formData.append("tideCenterLat", parseFloat(tideCenterLat));
          formData.append("tideCenterLon", parseFloat(tideCenterLon));
        }
        if (filterTracksNearArea) {
          formData.append("filterRadiusNm", parseFloat(filterRadiusNm));
        }
        appendMaxDepthParams(formData);
        setCsvProgress({
          progress: 0,
          message: "Uploading CSV...",
          status: "QUEUED",
        });
        startCsvConversion(formData).then((response) => {
          if (response.data?.jobId) {
            setCsvJobId(response.data.jobId);
          } else {
            setCsvProgress(undefined);
            setCsvError("Failed to start CSV conversion.");
            setOpen(false);
          }
        });
      } else if (isGpxFile(csbFile)) {
        if (correctMllw || applyWaterLevelCorrection) {
          const latNum = parseFloat(tideCenterLat);
          const lonNum = parseFloat(tideCenterLon);
          const hasArea =
            tideCenterLat.trim() !== "" &&
            tideCenterLon.trim() !== "" &&
            !Number.isNaN(latNum) &&
            !Number.isNaN(lonNum);
          if (tideCenterLat.trim() !== "" || tideCenterLon.trim() !== "") {
            if (!hasArea) {
              setCsvError(
                "Enter both latitude and longitude, or leave both blank for per-point tide stations."
              );
              setOpen(false);
              return;
            }
          }
        }

        const formData = new FormData();
        formData.append("file", csbFile);
        formData.append("fileName", csbFile.name);
        formData.append("correctMllw", correctMllw ? "true" : "false");
        formData.append(
          "smoothDepthSpikes",
          smoothDepthSpikes ? "true" : "false"
        );
        formData.append(
          "applyWaterLevelCorrection",
          applyWaterLevelCorrection ? "true" : "false"
        );
        formData.append("eleUnits", gpxEleUnits);
        if (tideCenterLat.trim() !== "" && tideCenterLon.trim() !== "") {
          formData.append("tideCenterLat", parseFloat(tideCenterLat));
          formData.append("tideCenterLon", parseFloat(tideCenterLon));
        }
        appendMaxDepthParams(formData);
        setGpxSubmitting(true);
        fetch(`${API_BASE}tracks/convert-gpx`, {
          method: "POST",
          body: formData,
        })
          .then(async (response) => {
            if (!response.ok) {
              throw new Error("GPX conversion failed.");
            }
            const blob = await response.blob();
            const disposition =
              response.headers.get("Content-Disposition") || "";
            downloadBlob(parseFilename(disposition), blob);
            if (checked) {
              dispatch(setFile(await blob.text()));
            }
            setOpen(false);
          })
          .catch((error) => {
            setCsvError(error.message || "GPX conversion failed.");
            setOpen(false);
          })
          .finally(() => setGpxSubmitting(false));
      } else {
        const formData = new FormData();
        formData.append("file", csbFile);
        formData.append("fileName", csbFile.name);
        upload(formData).then((response) => {
          complete(response.data);
        });
      }
    } else {
      setSubmitUrl(true);
    }
  };

  React.useEffect(() => {
    if (data) {
      complete(data);
    }
    // eslint-disable-next-line
  }, [data]);

  React.useEffect(() => {
    if (!csvJobId) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      try {
        const response = await fetch(
          `${API_BASE}tracks/convert-csv/jobs/${csvJobId}`
        );
        if (!response.ok) {
          throw new Error("Conversion job not found.");
        }

        const job = await response.json();
        if (cancelled) {
          return;
        }

        setCsvProgress({
          progress: job.progress,
          message: job.message,
          status: job.status,
        });

        if (job.status === "COMPLETE") {
          const result = await downloadCsvConversionJob(csvJobId);
          if (cancelled) {
            return;
          }
          setCsvJobId(undefined);
          setCsvProgress(undefined);
          // Summary comes from the job status payload (not download headers),
          // so large multi-track CSB jobs do not blow Tomcat's header limit.
          completeCsv({ ...result, summary: job.summary || result.summary });
          return;
        }

        if (job.status === "FAILED") {
          setCsvError(job.error || job.message || "Conversion failed.");
          setCsvJobId(undefined);
          setCsvProgress(undefined);
          setOpen(false);
          return;
        }

        timeoutId = window.setTimeout(poll, 500);
      } catch (error) {
        if (!cancelled) {
          setCsvError(error.message || "Conversion failed.");
          setCsvJobId(undefined);
          setCsvProgress(undefined);
          setOpen(false);
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line
  }, [csvJobId]);

  const complete = (transformed) => {
    let name = transformed.substring(
      transformed.indexOf("<name>") + 6,
      transformed.indexOf("</name>")
    );
    downloadFile(name, transformed);
    dispatch(setFile(transformed));
  };

  const completeCsv = async ({ blob, disposition, contentType, summary }) => {
    const filename = parseFilename(disposition);
    const isZip =
      contentType.includes("zip") || filename.toLowerCase().endsWith(".zip");
    downloadBlob(filename, blob);
    if (summary) {
      setCsvSummary({ ...summary, downloadedFilename: filename, isZip });
    }

    if (isZip) {
      setOpen(false);
      return;
    }

    if (checked) {
      const text = await blob.text();
      dispatch(setFile(text));
    }
    setOpen(false);
  };

  React.useEffect(() => {
    if (file) {
      setUrl(undefined);
      setCsbFile(undefined);
      setOpen(false);
      if (checked) {
        navigate("/");
      }
    }
    // eslint-disable-next-line
  }, [file]);

  const action = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  return (
    <div>
      <Typography variant="h4" mb={5}>
        CSB2GPX Converter
      </Typography>
      <Typography>
        This conversion utility is for those interested in creating *.gpx files
        suitable for import to{" "}
        <Link
          underline="hover"
          href="https://www.aquamap.app"
          target="_blank"
          rel="noreferrer"
        >
          AquaMap
        </Link>{" "}
        from NOAA{" "}
        <Link
          underline="hover"
          href="https://www.ncei.noaa.gov/maps/iho_dcdb/"
          target="_blank"
          rel="noreferrer"
        >
          CSB (Crowdsourced Bathymetry)
        </Link>{" "}
        files.{" "}
      </Typography>
      <br />
      <Typography>
        NOAA now offers two CSB download formats from the{" "}
        <Link
          underline="hover"
          href="https://www.ncei.noaa.gov/maps/bathymetry/"
          target="_blank"
          rel="noreferrer"
        >
          Bathymetric Data Viewer
        </Link>
        :
      </Typography>
      <br />
      <Typography component="div">
        <ul>
          <li>
            <strong>CSB Point Store CSV</strong> (new) — draw a box on the map,
            click &quot;Extract CSB Point Store Data&quot;, and upload the CSV
            on this page (not the GPX Viewer). AquaMap track CSV exports are
            also supported. By default the converter performs format conversion
            only — no smoothing and no dropped soundings. Enable MLLW or
            max-depth cutoff below when you need corrected output for AquaMap.
          </li>
          <li>
            <strong>CSB archive (.tar.gz)</strong> (legacy) — paste the NCEI
            pickup URL or upload the downloaded archive below.
          </li>
        </ul>
      </Typography>
      <br />
      <Typography>
        You can find instructions on how to download CSB files from
        Bob423&apos;s{" "}
        <Link underline="hover" href="https://bobicw.blogspot.com">
          ICW Tracks and Routes
        </Link>{" "}
        website page{" "}
        <Link
          underline="hover"
          href="https://bobicw.blogspot.com/p/crowd-sourced-bathymetry.html"
          target="_blank"
          rel="noreferrer"
        >
          here
        </Link>{" "}
        (note you will need to join the group before the link will take you to
        the post).
      </Typography>
      <br />
      <Typography>
        When you receive your NCEI Data Extraction email, copy the link it
        contains and paste below (it should look similar to
        https://www.ngdc.noaa.gov/next-web/rest/orders/506346/pickup).
      </Typography>

      {!csbFile && (
        <FormControl sx={{ mt: 2, mr: 4 }} variant="outlined" fullWidth>
          <InputLabel htmlFor="url">Paste URL here</InputLabel>
          <OutlinedInput
            id="url"
            type={"text"}
            onChange={(event) => {
              setUrl(event.target.value);
            }}
            size="medium"
            label="Paste URL here"
            value={url}
          />
        </FormControl>
      )}
      <br />
      <br />
      <Stack direction="row" alignItems="center" spacing={2} mb={5}>
        <form onSubmit={handleSubmit}>
          {!csbFile && !url && (
            <div>
              <Typography>If you have already downloaded the file:</Typography>
              <br />
              <label htmlFor="upload-file">
                <Button variant="outlined" component="span" size="large">
                  Select File
                </Button>
                <input
                  id="upload-file"
                  hidden
                  accept=".csv,application/gzip,application/x-gzip,.gz,.tar.gz,.gpx,application/gpx+xml"
                  type="file"
                  onChange={handleFile}
                />
              </label>
            </div>
          )}
          {csbFile && <Typography>File: {csbFile.name}</Typography>}
          {csbFile && isCsvFile(csbFile) && (
            <Paper
              sx={{ p: 2, mt: 2, mb: 1, maxWidth: 900 }}
              variant="outlined"
            >
              <Typography variant="subtitle2" gutterBottom>
                Raw CSB → GPX (default)
              </Typography>
              <Typography component="ol" variant="body2" sx={{ pl: 2, mb: 2 }}>
                <li>Select output format below</li>
                <li>Leave correction options unchecked for raw soundings</li>
                <li>
                  Optionally enable max-depth cutoff to exclude deep spikes from
                  export
                </li>
                <li>
                  Click Submit — unzip the ZIP if you chose separate files
                </li>
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                MLLW / water-level corrected GPX
              </Typography>
              <Typography component="ol" variant="body2" sx={{ pl: 2, mb: 0 }}>
                <li>Enable MLLW and/or water-level correction below</li>
                <li>
                  Set your area of interest (CSB) or leave blank for GPX
                  per-point stations
                </li>
                <li>Click Submit — import corrected GPX into AquaMap</li>
              </Typography>
            </Paper>
          )}
          {csbFile && (isCsvFile(csbFile) || isGpxFile(csbFile)) && (
            <FormControl sx={{ mt: 3 }}>
              {isGpxFile(csbFile) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Upload a single AquaMap GPX track. With no area coordinates,
                  MLLW correction uses the nearest NOAA tide station at each
                  sounding. Optionally enter lat/lon to use one station for the
                  whole track.
                </Typography>
              )}
              {isGpxFile(csbFile) && (
                <FormLabel id="gpx-ele-units-label">
                  Depth units stored in this GPX
                </FormLabel>
              )}
              {isGpxFile(csbFile) && (
                <RadioGroup
                  row
                  aria-labelledby="gpx-ele-units-label"
                  name="gpx-ele-units"
                  value={gpxEleUnits}
                  onChange={(event) => setGpxEleUnits(event.target.value)}
                >
                  <FormControlLabel
                    value="auto"
                    control={<Radio />}
                    label="Auto-detect (AquaMap tracks from 2026 on = meters)"
                  />
                  <FormControlLabel
                    value="feet"
                    control={<Radio />}
                    label="Feet"
                  />
                  <FormControlLabel
                    value="meters"
                    control={<Radio />}
                    label="Meters"
                  />
                </RadioGroup>
              )}
              {isCsvFile(csbFile) && (
                <FormLabel id="csv-output-mode-label">
                  CSV output format
                </FormLabel>
              )}
              {isCsvFile(csbFile) && (
                <RadioGroup
                  row
                  aria-labelledby="csv-output-mode-label"
                  name="csv-output-mode"
                  value={csvOutputMode}
                  onChange={(event) => setCsvOutputMode(event.target.value)}
                >
                  <FormControlLabel
                    value="separate"
                    control={<Radio />}
                    label="Separate GPX files (ZIP) — one .gpx per track"
                  />
                  <FormControlLabel
                    value="combined"
                    control={<Radio />}
                    label="Single GPX with all tracks in one file"
                  />
                </RadioGroup>
              )}
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={applyMaxDepthCutoff}
                    onChange={(event) =>
                      setApplyMaxDepthCutoff(event.target.checked)
                    }
                  />
                }
                label="Exclude soundings deeper than max depth (optional spike cutoff for AquaMap)"
              />
              {applyMaxDepthCutoff && (
                <TextField
                  label="Max depth cutoff"
                  value={maxDepthCutoffFt}
                  onChange={(event) => setMaxDepthCutoffFt(event.target.value)}
                  size="small"
                  sx={{ mt: 1, maxWidth: 200 }}
                  helperText="Feet — drops deep spikes from export, not minimums"
                />
              )}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Options (off by default)
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={correctMllw}
                    onChange={(event) => setCorrectMllw(event.target.checked)}
                  />
                }
                label="Correct depths to MLLW using NOAA tide station"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={smoothDepthSpikes}
                    onChange={(event) =>
                      setSmoothDepthSpikes(event.target.checked)
                    }
                  />
                }
                label="Smooth CSB depth spikes before MLLW correction (optional)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyWaterLevelCorrection}
                    onChange={(event) =>
                      setApplyWaterLevelCorrection(event.target.checked)
                    }
                    disabled={!correctMllw}
                  />
                }
                label="Apply water-level / wind-tide correction (requires MLLW)"
              />
              {isCsvFile(csbFile) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterTracksNearArea}
                      onChange={(event) =>
                        setFilterTracksNearArea(event.target.checked)
                      }
                    />
                  }
                  label="Exclude pass-through tracks (start and end must be near area of interest)"
                />
              )}
              {(needsAreaOfInterest ||
                (isGpxFile(csbFile) &&
                  (correctMllw || applyWaterLevelCorrection))) && (
                <Stack spacing={2} sx={{ mt: 2, maxWidth: 720 }}>
                  <Typography variant="body2" color="text.secondary">
                    {isGpxFile(csbFile)
                      ? "Optional: enter coordinates to use one tide station for the whole track. Leave blank for per-point station lookup along the track."
                      : "NOAA CSB exports include every track that passes through your search area, often extending far beyond it. The coordinates below are prefilled with the center of all points in your file — adjust them to your actual work area (for example, the ICW near a daymark). This point is used to pick the tide station and, when filtering is enabled, to drop routes that only pass through."}
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "flex-start" }}
                  >
                    <TextField
                      label={
                        isGpxFile(csbFile)
                          ? "Tide station latitude (optional)"
                          : "Area of interest latitude"
                      }
                      value={tideCenterLat}
                      onChange={(event) => setTideCenterLat(event.target.value)}
                      size="small"
                      required={needsAreaOfInterest}
                      placeholder="31.0900"
                      helperText="Decimal degrees (north positive)"
                    />
                    <TextField
                      label={
                        isGpxFile(csbFile)
                          ? "Tide station longitude (optional)"
                          : "Area of interest longitude"
                      }
                      value={tideCenterLon}
                      onChange={(event) => setTideCenterLon(event.target.value)}
                      size="small"
                      required={needsAreaOfInterest}
                      placeholder="-81.3500"
                      helperText="Decimal degrees (west negative)"
                    />
                    {filterTracksNearArea && (
                      <TextField
                        label="Track filter radius (nm)"
                        value={filterRadiusNm}
                        onChange={(event) =>
                          setFilterRadiusNm(event.target.value)
                        }
                        size="small"
                        required
                        placeholder="8"
                        helperText="Start and end must be within this distance"
                        sx={{ maxWidth: 200 }}
                      />
                    )}
                  </Stack>
                </Stack>
              )}
            </FormControl>
          )}
          {(url || csbFile) && (
            <Stack direction="row" alignItems="center" spacing={2} mt={5}>
              <Button
                variant="outlined"
                type={"submit"}
                size="large"
                disabled={csvConverting || gpxSubmitting}
              >
                Submit
              </Button>
              {(!isCsvFile(csbFile) || csvOutputMode === "combined") && (
                <FormControlLabel
                  control={
                    <Checkbox checked={checked} onChange={handleChange} />
                  }
                  label="Automatically open GPX file in viewer after download"
                />
              )}
            </Stack>
          )}
        </form>
      </Stack>
      {csvProgress && (
        <Paper sx={{ p: 3, mb: 4, maxWidth: 900 }} variant="outlined">
          <Typography variant="subtitle1" gutterBottom>
            Converting CSV
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {csvProgress.message}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={csvProgress.progress}
            sx={{ mt: 2, mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {csvProgress.progress}%
          </Typography>
        </Paper>
      )}
      {csvError && (
        <Alert severity="error" sx={{ mb: 4, maxWidth: 900 }}>
          {csvError}
        </Alert>
      )}
      {csvSummary && (
        <Paper sx={{ p: 3, mb: 5, maxWidth: 900 }} variant="outlined">
          <Typography variant="h6" gutterBottom>
            Conversion summary
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Downloaded{" "}
            {csvSummary.downloadedFilename || csvSummary.outputFilename}
          </Typography>
          {csvSummary.isZip && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Unzip this file to get <strong>{csvSummary.trackCount}</strong>{" "}
              separate GPX files
              {csvSummary.mllwCorrected
                ? ", each with MLLW-corrected depths"
                : ""}
              . Import them into AquaMap individually or as needed.
            </Alert>
          )}
          <Stack spacing={1} sx={{ mt: 2, mb: 3 }}>
            <Typography>
              <strong>{csvSummary.pointCount}</strong> soundings across{" "}
              <strong>{csvSummary.trackCount}</strong>{" "}
              {csvSummary.trackCount === 1 ? "track" : "tracks"}
              {csvSummary.excludedTrackCount > 0 && (
                <>
                  {" "}
                  ({csvSummary.excludedTrackCount} pass-through{" "}
                  {csvSummary.excludedTrackCount === 1 ? "track" : "tracks"}{" "}
                  excluded
                  {csvSummary.trackFilterRadiusNm != null
                    ? ` within ${csvSummary.trackFilterRadiusNm} nm`
                    : ""}
                  )
                </>
              )}
            </Typography>
            <Typography>
              Date range: {formatTimestamp(csvSummary.earliestTime)} to{" "}
              {formatTimestamp(csvSummary.latestTime)}
            </Typography>
            <Typography>
              Depth range: {formatDepth(csvSummary.minDepthFt)} to{" "}
              {formatDepth(csvSummary.maxDepthFt)}
              {csvSummary.mllwCorrected ? " (MLLW corrected)" : ""}
            </Typography>
            {csvSummary.maxDepthCutoffApplied && (
              <Typography>
                Max-depth cutoff: excluded {csvSummary.maxDepthPointsExcluded}{" "}
                sounding(s) deeper than{" "}
                {formatDepth(csvSummary.maxDepthCutoffFt)}
              </Typography>
            )}
            {!csvSummary.mllwCorrected && (
              <Typography color="text.secondary">
                Raw soundings — no MLLW correction applied
              </Typography>
            )}
            {csvSummary.mllwCorrected && (
              <>
                {csvSummary.tideReferenceLat != null &&
                  csvSummary.tideReferenceLon != null && (
                    <Typography>
                      Area of interest: {csvSummary.tideReferenceLat.toFixed(4)}
                      °, {csvSummary.tideReferenceLon.toFixed(4)}°
                    </Typography>
                  )}
                {csvSummary.tracksFilteredNearArea &&
                  csvSummary.excludedTrackCount > 0 && (
                    <Typography>
                      Track filter: kept routes whose start and end are within{" "}
                      {csvSummary.trackFilterRadiusNm} nm of the area of
                      interest
                    </Typography>
                  )}
                <Typography>
                  Tide station: {csvSummary.tideStationName}
                  {csvSummary.tideStationId &&
                  !csvSummary.tideStationId.includes("stations")
                    ? ` (${csvSummary.tideStationId})`
                    : ""}
                </Typography>
                <Typography>
                  Raw depth range: {formatDepth(csvSummary.rawMinDepthFt)} to{" "}
                  {formatDepth(csvSummary.rawMaxDepthFt)}
                </Typography>
                <Typography>
                  Corrected {csvSummary.tideCorrectedPoints} of{" "}
                  {csvSummary.pointCount} soundings
                  {csvSummary.tideSkippedPoints > 0
                    ? ` (${csvSummary.tideSkippedPoints} left uncorrected)`
                    : ""}
                </Typography>
                {csvSummary.depthSpikesSmoothed &&
                  csvSummary.spikePointsAdjusted > 0 && (
                    <Typography>
                      Smoothed {csvSummary.spikePointsAdjusted} CSB depth
                      spike(s) before correction (raw min{" "}
                      {formatDepth(csvSummary.spikeRawMinDepthFt)}, smoothed min{" "}
                      {formatDepth(csvSummary.spikeSmoothedMinDepthFt)})
                    </Typography>
                  )}
                {csvSummary.waterLevelCorrected && (
                  <Typography>
                    Water level station: {csvSummary.waterLevelStationName}
                    {csvSummary.waterLevelStationId
                      ? ` (${csvSummary.waterLevelStationId})`
                      : ""}
                    {csvSummary.waterLevelCorrectedPoints != null
                      ? ` — ${csvSummary.waterLevelCorrectedPoints} soundings adjusted`
                      : ""}
                  </Typography>
                )}
              </>
            )}
            <Typography>
              Platforms: {csvSummary.platforms.join(", ")}
            </Typography>
            <Typography>
              Providers: {csvSummary.providers.join(", ")}
            </Typography>
          </Stack>
          {(csvSummary.tracks.length > 1 || csvSummary.mllwCorrected) && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Track</TableCell>
                  <TableCell align="right">Points</TableCell>
                  <TableCell>Platform</TableCell>
                  {csvSummary.mllwCorrected && (
                    <TableCell>Tide station</TableCell>
                  )}
                  <TableCell>Time range</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvSummary.tracks.map((track) => (
                  <TableRow key={track.trackId}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {track.trackId}
                    </TableCell>
                    <TableCell align="right">{track.pointCount}</TableCell>
                    <TableCell>{track.platformName}</TableCell>
                    {csvSummary.mllwCorrected && (
                      <TableCell>
                        {track.tideStationName || "—"}
                        {track.tideStationId ? ` (${track.tideStationId})` : ""}
                        {track.tideStationDistanceNm != null
                          ? `, ${track.tideStationDistanceNm.toFixed(1)} nm`
                          : ""}
                      </TableCell>
                    )}
                    <TableCell>
                      {formatTimestamp(track.earliestTime)} –{" "}
                      {formatTimestamp(track.latestTime)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
      <br />
      <TideLookup />
      <Typography variant="body2" color="text.secondary">
        The GPX Viewer (link below) is only for inspecting an already-downloaded
        GPX file. To create or re-download corrected tracks from a CSV, stay on
        this page, upload your CSV again, and use Submit.
      </Typography>
      <br />
      <Typography>
        Open the GPX Viewer to inspect a file:{" "}
        <Link underline="hover" href="/" rel="noreferrer">
          GPX Viewer
        </Link>
      </Typography>

      <Snackbar
        open={open && !csvConverting}
        autoHideDuration={6000}
        onClose={handleClose}
        action={action}
      >
        <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
          Processing file
        </Alert>
      </Snackbar>
    </div>
  );
}
